// Shared by the generic LDAP connector and the Microsoft Active Directory
// connector, which both ride the `ldap` proxy protocol and differ only in
// branding and defaults.

const LDAP_PASSWORD_PLACEHOLDER = "__3B_LDAP_PASSWORD_PLACEHOLDER__";

export type LdapDefaults = { port: string; tls: "implicit" | "off" };

function hostComponent(host: string | undefined) {
  const value = (host ?? "").trim();
  if (/^\[[0-9a-fA-F:.]+\]$/.test(value)) return value;
  if (!/^[A-Za-z0-9.-]+$/.test(value)) {
    throw new Error("Invalid LDAP host");
  }
  return value;
}

function portComponent(port: string | undefined, defaultPort: string) {
  const value = (port || defaultPort).trim();
  if (!/^\d+$/.test(value)) throw new Error("Invalid LDAP port");
  const portNumber = Number(value);
  if (portNumber < 1 || portNumber > 65535) {
    throw new Error("Invalid LDAP port");
  }
  return value;
}

// StartTLS isn't supported by the relay, so the connector offers LDAPS or none.
function tlsComponent(tls: string | undefined, defaultTls: "implicit" | "off") {
  return tls === "implicit" || tls === "off" ? tls : defaultTls;
}

export function connectionString(
  { host, port, username, tls }: Record<string, string>,
  defaults: LdapDefaults
) {
  const ldapHost = hostComponent(host);
  const ldapPort = portComponent(port, defaults.port);
  const scheme =
    tlsComponent(tls, defaults.tls) === "implicit" ? "ldaps" : "ldap";
  const encodedBindDn = encodeURIComponent(username ?? "");
  return `${scheme}://${encodedBindDn}:${LDAP_PASSWORD_PLACEHOLDER}@${ldapHost}:${ldapPort}`;
}

export function publicEnvVars(
  { host, port, username, tls }: Record<string, string>,
  defaults: LdapDefaults
) {
  return {
    LDAP_HOST: hostComponent(host),
    LDAP_PORT: portComponent(port, defaults.port),
    LDAP_BIND_DN: username ?? "",
    LDAP_PASSWORD: LDAP_PASSWORD_PLACEHOLDER,
    LDAP_TLS: tlsComponent(tls, defaults.tls),
  };
}
